// PropertyTenantsPage.tsx - Page for managing tenants of a specific property
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  EnvelopeIcon,
  CalendarIcon,
  CheckCircleIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import toast from 'react-hot-toast';
import AddTenant from '../../components/modals/AddTenant';
import BroadcastModal from '../../components/messages/BroadcastModal';
import EditTenancyModal, { EditTenancyForm } from './components/EditTenancyModal';
import SubTenantModal, { SubTenantForm } from './components/SubTenantModal';
import RemoveTenantModal from './components/RemoveTenantModal';
import TenantCard from './components/TenantCard';
import ActionBar from './components/ActionBar';
import { Tenant } from '../../types/tenancyTypes';
import LandlordToolkitLayout from '../../components/landlord-toolkit/LandlordToolkitLayout';

interface PropertyDetails {
  id: number;
  title: string;
  address: string;
  type: string;
}

const PropertyTenantsPage: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [property, setProperty] = useState<PropertyDetails | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addTenantModalOpen, setAddTenantModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [removeTenantModalOpen, setRemoveTenantModalOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editTenancyModalOpen, setEditTenancyModalOpen] = useState(false);
  const [savingEditTenancy, setSavingEditTenancy] = useState(false);
  const [editSubTenantModalOpen, setEditSubTenantModalOpen] = useState(false);
  const [savingSubTenant, setSavingSubTenant] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [editForm, setEditForm] = useState<{
    rentAmount?: number;
    depositAmount?: number;
    leaseEndDate?: string;
    rentDueDay?: number;
    status?: 'active' | 'inactive';
    rtbRegisteredOn?: string;
  }>({});
  const [editSubTenantForm, setEditSubTenantForm] = useState<{
    id?: string;
    name: string;
    email: string;
    phone?: string;
  }>({ name: '', email: '' });

  const fetchPropertyAndTenants = useCallback(
    async (showLoader = false) => {
      if (!currentUser || !propertyId) return;

      setError(null);
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getPropertyTenants?propertyId=${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load tenants');
        }

        setProperty(data.property);
        setTenants(data.tenants || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load property tenants');
        toast.error(err.message || 'Failed to load property tenants');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [currentUser, propertyId]
  );

  useEffect(() => {
    fetchPropertyAndTenants(true);
  }, [fetchPropertyAndTenants]);

  const openRemoveTenantModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setRemoveTenantModalOpen(true);
  };

  const openEditTenancyModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);

    let rtbDate = '';
    if (tenant.rtbRegisteredOn) {
      rtbDate = new Date(tenant.rtbRegisteredOn._seconds * 1000).toISOString().split('T')[0];
    }

    setEditForm({
      rentAmount: tenant.rentAmount,
      depositAmount: tenant.depositAmount,
      leaseEndDate: tenant.leaseEndDate || '',
      rentDueDay: tenant.rentDueDay,
      status: tenant.status,
      rtbRegisteredOn: rtbDate,
    });
    setEditTenancyModalOpen(true);
  };

  const openEditSubTenantModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setEditSubTenantForm({ name: '', email: '', phone: '' });
    setEditSubTenantModalOpen(true);
  };

  const closeRemoveTenantModal = () => {
    setSelectedTenant(null);
    setRemoveTenantModalOpen(false);
    setRemoving(false);
  };

  const handleRemoveTenant = async () => {
    if (!currentUser || !selectedTenant || !propertyId) return;

    setRemoving(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/removeTenantFromProperty`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: Number(propertyId),
          tenantId: selectedTenant.id,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove tenant');
      }

      toast.success('Tenant removed successfully');
      closeRemoveTenantModal();
      await fetchPropertyAndTenants();
    } catch (err: any) {
      console.error('Failed to remove tenant:', err);
      toast.error(err.message || 'Failed to remove tenant');
    } finally {
      setRemoving(false);
    }
  };

  const handleEditTenancy = async () => {
    if (!currentUser || !selectedTenant || !propertyId) return;
    setSavingEditTenancy(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/editTenancy`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenancyId: selectedTenant.id,
          propertyId: Number(propertyId),
          updates: editForm,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to edit tenancy');
      toast.success('Tenancy updated');
      setEditTenancyModalOpen(false);
      setSelectedTenant(null);
      await fetchPropertyAndTenants();
    } catch (err: any) {
      toast.error(err.message || 'Failed to edit tenancy');
    } finally {
      setSavingEditTenancy(false);
    }
  };

  const handleAddOrEditSubTenant = async () => {
    if (!currentUser || !selectedTenant || !propertyId) return;
    if (!editSubTenantForm.name.trim() || !editSubTenantForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    try {
      setSavingSubTenant(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/addOrEditSubTenant`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenancyId: selectedTenant.id,
          propertyId: Number(propertyId),
          subTenant: editSubTenantForm,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save sub-tenant');
      toast.success('Sub-tenant saved');
      setEditSubTenantModalOpen(false);
      setSelectedTenant(null);
      await fetchPropertyAndTenants();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save sub-tenant');
    } finally {
      setSavingSubTenant(false);
    }
  };

  const handleSendBroadcast = async (subject?: string, message?: string) => {
    const finalSubject = (subject ?? broadcastSubject).trim();
    const finalMessage = (message ?? broadcastMessage).trim();

    if (!currentUser || !property || !finalSubject || !finalMessage) {
      console.error('Broadcast validation failed:', {
        currentUser: !!currentUser,
        property: !!property,
        finalSubject,
        finalMessage,
      });
      toast.error('Please fill in both subject and message');
      return;
    }
    setSendingBroadcast(true);
    try {
      const token = await currentUser.getIdToken();
      const payload = {
        listingId: Number(property.id),
        subject: finalSubject,
        message: finalMessage,
      };
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendBroadcastToTenants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send broadcast');
      setShowBroadcastModal(false);
      setBroadcastSubject('');
      setBroadcastMessage('');
      toast.success(`Broadcast sent successfully to ${data.data?.recipientCount ?? 'tenants'}!`);
    } catch (err: any) {
      console.error('Broadcast error:', err);
      toast.error(err.message || 'Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const activeTenants = tenants.filter((t) => t.status === 'active');
  const inactiveTenants = tenants.filter((t) => t.status === 'inactive');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!currentUser) return null;

  return (
    <LandlordToolkitLayout title={property ? property.title : 'Property Tenants'}>
      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/landlord-toolkit/managed-properties')}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Portfolio
          </button>

          {loading && !property ? (
            <div className="animate-pulse">
              <div className="h-8 w-64 bg-gray-200 rounded mb-2" />
              <div className="h-5 w-48 bg-gray-100 rounded" />
            </div>
          ) : property ? (
            <>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
              <p className="text-gray-600">{property.address}</p>
            </>
          ) : null}
        </div>

        {/* Action Bar */}
        <ActionBar
          refreshing={refreshing}
          canAddTenancy={!!property}
          canComposeBroadcast={!!property}
          onRefresh={() => fetchPropertyAndTenants()}
          onAddTenancy={() => setAddTenantModalOpen(true)}
          onComposeBroadcast={() => setShowBroadcastModal(true)}
        />

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <div className="rounded-2xl border border-white/60 bg-white/80 p-6 shadow-lg shadow-blue-100 backdrop-blur">
            <div className="mb-4 inline-flex rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 p-3 text-white">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Active Tenancy
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-gray-900">{activeTenants.length}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/60 bg-white/80 p-6 shadow-lg shadow-gray-100 backdrop-blur">
            <div className="mb-4 inline-flex rounded-2xl bg-gradient-to-br from-gray-500 to-gray-600 p-3 text-white">
              <UserMinusIcon className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Inactive Tenancy
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-gray-900">{inactiveTenants.length}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/60 bg-white/80 p-6 shadow-lg shadow-blue-100 backdrop-blur">
            <div className="mb-4 inline-flex rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 p-3 text-white">
              <CalendarIcon className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Total Tenants
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-gray-900">{tenants.length}</p>
            </div>
          </div>
        </div>

        {/* Tenants List */}
        <div className="rounded-3xl bg-white p-6 shadow-xl">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-1/3 rounded bg-gray-200" />
              <div className="h-32 rounded-2xl bg-gray-100" />
              <div className="h-32 rounded-2xl bg-gray-100" />
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
              {error}
            </div>
          ) : tenants.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center">
              <UserPlusIcon className="h-12 w-12 text-gray-400" />
              <div>
                <p className="text-lg font-semibold text-gray-900">No tenants yet</p>
                <p className="text-gray-500">Add a tenant to start managing this property.</p>
              </div>
              <button
                onClick={() => setAddTenantModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/40 transition hover:scale-[1.01]"
              >
                <UserPlusIcon className="h-5 w-5" />
                Add First Tenant
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Tenants */}
              {activeTenants.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Active Tenancy ({activeTenants.length})
                  </h3>
                  <div className="space-y-4">
                    {activeTenants.map((tenant) => (
                      <TenantCard
                        key={tenant.id}
                        tenant={tenant}
                        propertyId={property?.id ?? Number(propertyId)}
                        onEditTenancy={openEditTenancyModal}
                        onEditSubTenant={openEditSubTenantModal}
                        onRemoveTenant={openRemoveTenantModal}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Inactive Tenants */}
              {inactiveTenants.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Inactive Tenancy ({inactiveTenants.length})
                  </h3>
                  <div className="space-y-4">
                    {inactiveTenants.map((tenant) => (
                      <div
                        key={tenant.id}
                        className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-gray-50/50 p-6 opacity-75 md:flex-row md:items-center"
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-3">
                            <h4 className="text-lg font-semibold text-gray-500">{tenant.name}</h4>
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                              Inactive
                            </span>
                          </div>
                          <div className="grid gap-2 md:grid-cols-2">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                              {tenant.email}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                              Moved in: {formatDate(tenant.moveInDate)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Tenant Modal */}
      {property && (
        <AddTenant
          isOpen={addTenantModalOpen}
          onClose={() => setAddTenantModalOpen(false)}
          propertyId={property.id}
          propertyTitle={property.title}
          onSuccess={() => {
            fetchPropertyAndTenants();
          }}
        />
      )}

      {/* Remove Tenant Modal */}
      {removeTenantModalOpen && selectedTenant && (
        <RemoveTenantModal
          isOpen={removeTenantModalOpen}
          tenantName={selectedTenant.name}
          onClose={closeRemoveTenantModal}
          onConfirm={handleRemoveTenant}
          removing={removing}
        />
      )}

      {/* Edit Tenancy Modal */}
      {editTenancyModalOpen && selectedTenant && (
        <EditTenancyModal
          isOpen={editTenancyModalOpen}
          tenantName={selectedTenant.name}
          editForm={editForm as EditTenancyForm}
          onChange={(updates) => setEditForm((p) => ({ ...p, ...updates }))}
          onClose={() => setEditTenancyModalOpen(false)}
          onSave={handleEditTenancy}
          saving={savingEditTenancy}
        />
      )}

      {/* Add/Edit Sub-tenant Modal */}
      {editSubTenantModalOpen && selectedTenant && (
        <SubTenantModal
          isOpen={editSubTenantModalOpen}
          form={editSubTenantForm as SubTenantForm}
          onChange={(updates) => setEditSubTenantForm((p) => ({ ...p, ...updates }))}
          onClose={() => setEditSubTenantModalOpen(false)}
          onSave={handleAddOrEditSubTenant}
          saving={savingSubTenant}
        />
      )}

      {/* Broadcast Modal (shared) */}
      {property && (
        <BroadcastModal
          isOpen={showBroadcastModal}
          onClose={() => setShowBroadcastModal(false)}
          recipientName={property.title || 'Tenants'}
          tenancies={tenants.map((tenant) => ({
            id: tenant.id,
            name: tenant.name,
          }))}
          initialSubject={broadcastSubject}
          onSend={(s, m) => handleSendBroadcast(s, m)}
          isSending={sendingBroadcast}
        />
      )}
    </LandlordToolkitLayout>
  );
};

export default PropertyTenantsPage;
